import { ColorScheme } from '../components/ColorSchemeEditor/types';
import { getDefaultColorScheme } from '../components/ColorSchemeEditor/colorVariables';

/**
 * Theme Service - Business Logic Layer
 * Handles theme persistence, validation, and application
 */

const THEME_STORAGE_KEY = 'custom-color-scheme';

export interface ThemeServiceResult<T = any> {
  success: boolean;
  data?: T;
  error?: string;
}

/**
 * Save a color scheme to localStorage
 */
export const saveColorScheme = async (scheme: ColorScheme): Promise<ThemeServiceResult<ColorScheme>> => {
  try {
    // Validate scheme structure
    if (!scheme.id || !scheme.name || !scheme.colors) {
      return {
        success: false,
        error: 'Invalid color scheme structure'
      };
    }

    // Validate color format (basic validation)
    const validateColors = (colors: Record<string, string>) => {
      for (const [key, value] of Object.entries(colors)) {
        if (!key.startsWith('--') || !value.trim()) {
          return false;
        }
      }
      return true;
    };

    if (!validateColors(scheme.colors.light) || !validateColors(scheme.colors.dark)) {
      return {
        success: false,
        error: 'Invalid color format in scheme'
      };
    }

    // Save to localStorage
    localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(scheme));

    return {
      success: true,
      data: scheme
    };
  } catch (error) {
    return {
      success: false,
      error: `Failed to save color scheme: ${error instanceof Error ? error.message : 'Unknown error'}`
    };
  }
};

/**
 * Load saved color scheme from localStorage
 */
export const loadSavedColorScheme = (): ThemeServiceResult<ColorScheme | null> => {
  try {
    const saved = localStorage.getItem(THEME_STORAGE_KEY);
    if (!saved) {
      return {
        success: true,
        data: null
      };
    }

    const scheme = JSON.parse(saved) as ColorScheme;
    
    // Basic validation
    if (!scheme.id || !scheme.name || !scheme.colors) {
      throw new Error('Invalid saved color scheme structure');
    }

    return {
      success: true,
      data: scheme
    };
  } catch (error) {
    return {
      success: false,
      error: `Failed to load color scheme: ${error instanceof Error ? error.message : 'Unknown error'}`
    };
  }
};

/**
 * Apply color scheme to the DOM
 */
export const applyColorScheme = (scheme: ColorScheme, mode: 'light' | 'dark' = 'light'): ThemeServiceResult => {
  try {
    const root = document.documentElement;
    const colors = scheme.colors[mode];

    // Apply each color variable to the root element
    Object.entries(colors).forEach(([variable, value]) => {
      root.style.setProperty(variable, value);
    });

    return {
      success: true
    };
  } catch (error) {
    return {
      success: false,
      error: `Failed to apply color scheme: ${error instanceof Error ? error.message : 'Unknown error'}`
    };
  }
};

/**
 * Reset to default color scheme
 */
export const resetToDefaultColorScheme = (): ThemeServiceResult => {
  try {
    // Remove custom scheme from localStorage
    localStorage.removeItem(THEME_STORAGE_KEY);

    // Reset CSS variables to default
    const root = document.documentElement;
    const defaultScheme = getDefaultColorScheme();

    // Determine current theme mode
    const isDark = root.classList.contains('dark');
    const colors = isDark ? defaultScheme.dark : defaultScheme.light;

    // Apply default colors
    Object.entries(colors).forEach(([variable, value]) => {
      root.style.setProperty(`--${variable.replace('--', '')}`, value);
    });

    return {
      success: true
    };
  } catch (error) {
    return {
      success: false,
      error: `Failed to reset color scheme: ${error instanceof Error ? error.message : 'Unknown error'}`
    };
  }
};

/**
 * Export current color scheme as CSS
 */
export const exportColorSchemeAsCSS = (scheme: ColorScheme): string => {
  const lightColors = Object.entries(scheme.colors.light)
    .map(([variable, value]) => `    ${variable}: ${value};`)
    .join('\n');

  const darkColors = Object.entries(scheme.colors.dark)
    .map(([variable, value]) => `    ${variable}: ${value};`)
    .join('\n');

  return `/* ${scheme.name} - Generated Color Scheme */
/* ${scheme.description || 'Custom color scheme'} */

@layer base {
  :root {
${lightColors}
  }

  .dark {
${darkColors}
  }
}`;
};

/**
 * Get current color scheme (saved or default)
 */
export const getCurrentColorScheme = (): ColorScheme => {
  const saved = loadSavedColorScheme();
  
  if (saved.success && saved.data) {
    return saved.data;
  }

  // Return default scheme
  const defaultColors = getDefaultColorScheme();
  return {
    id: 'default',
    name: 'Default Theme',
    description: 'Default application color scheme',
    isDefault: true,
    colors: defaultColors
  };
};
